import {getCurrentLayout} from "../bvcomponents/appLayout";
import {featureThumbnailUrl, getCartItemObject} from "./helpers";
import store from "../store";
import {setActiveQuoteIndex, setQuotes} from "../actions/roomsFunctions";
import {setQtActiveCategory} from "../actions/actionFunctions";
import {AVAILABLE_VISUALIZERS, getCommonVisualizerType} from "../config";
import {getQuoteVisualizerType, hasSubscriptionForRoom} from "./visualizerSwitchingFunctions";
import {updateCurrentQuote} from "./quotesFunctions";
import {getWizardValuesReassignedToNewFlow, updateAllWizardValues} from "../actions/wizardFunctions";
import {changeAllCartItems} from "../actions/shoppingCartFunctions";
import {getUpdatedAllWizardValues} from "../reducers/wizardReducer";
import {isSameVisualizerWizard} from "../bvcomponents/look/lookNavigation/lookMain/lookWizard/lookWizardFunctions2";
import {
  getCorrectedWizardGroupForFeature,
  showWizardInsteadOfQT
} from "../bvcomponents/look/lookNavigation/lookMain/lookWizard/lookWizardFunctions";
import { getNavItems } from "../bvcomponents/sqft/helpers/sqftHelperUi";

/**
 * @param fabricatorState {object}
 * @param cartItem {cartItemInterface}
 * returns {boolean}
 */
export const doNotShowStaticCanvasImageFor = (fabricatorState, cartItem) => {
  return fabricatorState.isRenuit && cartItem && cartItem.cartFeature && cartItem.cartFeature.dbFeature && cartItem.cartFeature.dbFeature
    && cartItem.cartFeature.getLowerCasedCustomDisplayName
    && (cartItem.cartFeature.getLowerCasedCustomDisplayName().includes("no countertop")
      || cartItem.cartFeature.getLowerCasedCustomDisplayName().includes("countertop - yes")
      || cartItem.cartFeature.getLowerCasedCustomDisplayName().includes("countertop - no"))
}

/**
 * @param qtCatName {string}
 * @param quoteIndex {number}
 * returns {string}
 */
export const getRoomImageFromQtCategory = (qtCatName, quoteIndex) => {
  const {fabricatorState, cartState} = store.getState();
  if (!fabricatorState.settings.useCategorySwitching) {
    return ""
  }
  const lowerCasedVisualizerType = getCommonVisualizerType().toLowerCase();
  if (!qtCatName && cartState.cartItems.length && Number.isInteger(quoteIndex)) {
    const {cartItems} = cartState;
    const existingAddedCartItem = cartItems.find(cItem => cItem.partId === "room" && (cItem.currentQuote === quoteIndex));
    if (existingAddedCartItem
      && !doNotShowStaticCanvasImageFor(fabricatorState, existingAddedCartItem)
      && !featureIsMatchingQtCatName(existingAddedCartItem.cartFeature, lowerCasedVisualizerType)
      && (!fabricatorState.settings.allowVisualizerSwitching || !hasSubscriptionForRoom(getRoomNameToCompare(existingAddedCartItem.cartFeature)))) {
      return featureThumbnailUrl(existingAddedCartItem.cartFeature)
    }
    return ""
  }
  const currentRoomName = qtCatName.trim().toLowerCase();
  const layout = getCurrentLayout();
  const roomFg = layout.getFeatureGroupByPartId("room");
  if (roomFg) {
    const fgNavItems = layout.designNavigationItems({part: roomFg}, null, "", false, "all");
    const roomFeatureNavItem = fgNavItems.find(navItem => {
      return featureIsMatchingQtCatName(navItem.part, currentRoomName) && !featureIsMatchingQtCatName(navItem.part, lowerCasedVisualizerType)
    });
    if (roomFeatureNavItem) {
      return featureThumbnailUrl(roomFeatureNavItem.part)
    }
  }
  return ""
}

/**
 * @param cartItems {[cartItemInterface]}
 * @param quoteIndex {number}
 * returns {string}
 */
export const getCurrentQuoteRoomCartItem = (cartItems, quoteIndex) => {
  return cartItems.find(cItem => cItem.partId === "room" && cItem.currentQuote === quoteIndex)
}

/**
 * @param roomFeature {featureInterface}
 * returns {string}
 */
export const getRoomNameToCompare = (roomFeature) => {
  if (!roomFeature) {
    return ""
  }
  if (roomFeature.dbFeature?.sharedFeatureName && AVAILABLE_VISUALIZERS[roomFeature.dbFeature.sharedFeatureName]) {
    return roomFeature.dbFeature.sharedFeatureName.toLowerCase() // return Bathroom instead of Banos (de Stefano fix)
  }
  if (roomFeature.getLowerCasedCustomDisplayName) {
    return roomFeature.getLowerCasedCustomDisplayName() || roomFeature.dbFeature.sharedFeatureName || ""
  }
  return roomFeature.dbFeature.sharedFeatureName || ""
}

/**
 * @param cartItems {[cartItemInterface]}
 * @param quoteIndex {number}
 * @param withVisualizerCheck {boolean}
 * returns {string}
 */
export const getCurrentQuoteRoomName = (cartItems, quoteIndex, withVisualizerCheck = false) => {
  const quoteRoomCartItem = getCurrentQuoteRoomCartItem(cartItems, quoteIndex);
  if (quoteRoomCartItem && quoteRoomCartItem.cartFeature) {
    return getRoomNameToCompare(quoteRoomCartItem.cartFeature)
  }
  return withVisualizerCheck ? getCommonVisualizerType() : ""
}

/**
 * @param qtCategoryName {string}
 * @param fabricatorSettings {object}
 * returns {object}
 */
export const getRoomCartItemForCategoryName = (qtCategoryName, fabricatorSettings) => {
  const currentRoomName = qtCategoryName?.trim().toLowerCase();
  const layout = getCurrentLayout();
  const state = store.getState();

  //1.) get room from qtCategories
  if (layout.qtCategories) {
    for (let i = 0; i < layout.qtCategories.length; i++) {
      const qtCategory = layout.qtCategories[i];
      if (qtCategory.subParts) {
        const roomFg = qtCategory.subParts.find(subPart => subPart.part.partId === "room");
        if (roomFg) {
          const fgNavItems = layout.designNavigationItems(roomFg, null, "", false, true);
          let roomFeatureNavItem = fgNavItems.find(navItem => featureIsMatchingQtCatName(navItem.part, currentRoomName));
          if (!roomFeatureNavItem) {
            let settings = fabricatorSettings;
            if (!settings) {
              settings = store.getState().fabricatorState.settings;
            }
            if (settings.allowVisualizerSwitching) {
              const visualizerRoomName = getCommonVisualizerType(document.kvConfig.visualizerType);
              if (visualizerRoomName !== qtCategoryName) {
                // QCDES-3163 cases when categories are not the same as room names, but we still need to add Room Cart Item
                roomFeatureNavItem = fgNavItems.find(navItem => featureIsMatchingQtCatName(navItem.part, visualizerRoomName));
              }
            }
          }
          if (roomFeatureNavItem) {
            return getCartItemObject(roomFeatureNavItem.part, 1, qtCategory.name)
          }
        }
      }
    }
  }

  //2.) get room from wizard selected values
  const {wizardSelectedValues} = state.wizardState;
  const roomSelectedInTheWizard = Object.values(wizardSelectedValues).find(wizardSelectedValue => featureIsMatchingQtCatName(wizardSelectedValue.selectedFeature, currentRoomName));
  if (roomSelectedInTheWizard) {
    return getCartItemObject(roomSelectedInTheWizard.selectedFeature, 1, qtCategoryName);
  }

  //3.) get room from room Groups
  const roomsFeatureGroup = layout.getSubPartFromQtCategories('room');
  const roomsNavItems = getNavItems(roomsFeatureGroup, "", {}, false, true);

  if (roomsNavItems) {
    const roomFeatureNavItem = roomsNavItems.find(navItem => featureIsMatchingQtCatName(navItem.part, currentRoomName));
    if (roomFeatureNavItem) {
      return getCartItemObject(roomFeatureNavItem.part, 1, qtCategoryName);
    }
  }

  return null
}

/**
 * @param newCartItems {[cartItemInterface]}
 * @param setNewRoomIndex {boolean}
 * @param shouldCreateNewQuotes {boolean}
 * returns {[cartItemInterface]}
 */
export const updateQuoteRoomsForCartItems = async (newCartItems, setNewRoomIndex = false, shouldCreateNewQuotes = true) => {
  const {fabricatorState, qtState, cartState, quotesState, wizardState} = store.getState();
  if (!fabricatorState.settings.useCategorySwitching) {
    return newCartItems
  }
  const {qtActiveCategory} = qtState;
  const {cartItems} = cartState;
  const {quotes, activeQuoteIndex} = quotesState;
  const {wizardList, wizardIsActive} = wizardState;

  const createdRoomCartItems = [];
  const layout = getCurrentLayout();

  for (const newCartItem of [...newCartItems]) { // here we are changing array to prevent looping each time we add a new cart item below

    const withCreated = [...cartItems, ...createdRoomCartItems];

    const currentRoomName = newCartItem.qtCatName ? newCartItem.qtCatName.trim().toLowerCase() : qtActiveCategory.trim().toLowerCase();
    const existingCartRoom = getCurrentQuoteRoomCartItem(withCreated, activeQuoteIndex);

    console.log("existingCartRoom", existingCartRoom);

    if (existingCartRoom && existingCartRoom.cartFeature && (
      !showWizardInsteadOfQT(wizardList, wizardIsActive)
      && layout.qtCategories && layout.qtCategories.length > 1
      && !featureIsMatchingQtCatName(existingCartRoom.cartFeature, currentRoomName)) || (
      fabricatorState.settings.allowVisualizerSwitching && existingCartRoom && existingCartRoom.fromVisualizerType && (
        existingCartRoom.fromVisualizerType !== document.kvConfig.visualizerType
      )
    )) {

      if (!getRoomCartItemForCategoryName(currentRoomName, fabricatorState.settings)) { // this checks if room is available for current category
        break;
      }

      const existingAddedCartItem = withCreated.find(cItem => cItem.partId === "room" && featureIsMatchingQtCatName(cItem.cartFeature, currentRoomName));
      let newActiveQuoteIndex = 0;
      if (existingAddedCartItem) {
        newActiveQuoteIndex = existingAddedCartItem.currentQuote;
      } else if (shouldCreateNewQuotes) { // here we add a new room and add a new room cartItem
        const currentQuote = await updateCurrentQuote();
        const newQuotes = [...currentQuote, {}];
        newActiveQuoteIndex = quotes.length; // for example, if length is 2 (we are adding 2 index(0 and 1 already exist))
        store.dispatch(setQuotes(newQuotes));
        // console.log("newCreatedRooms", newQuotes);
        const roomCartItem = getRoomCartItemForCategoryName(currentRoomName, fabricatorState.settings);
        if (roomCartItem && !newCartItems.some(cItem => {
          // QCDES-3901 if we are already adding room to the cart - no need to add another room
          return (cItem.currentQuote === roomCartItem.currentQuote)
            && (cItem.partId === roomCartItem.partId)
            && (cItem.cartFeature.dbFeature.id === roomCartItem.cartFeature.dbFeature.id)
        })) {
          const newRoomCartItem = Object.assign({}, roomCartItem, {
            currentQuote: newActiveQuoteIndex
          })
          newCartItems.push(newRoomCartItem);
          createdRoomCartItems.push(newRoomCartItem);
        }
      }
      console.log("newActiveQuoteIndex", newActiveQuoteIndex);
      newCartItem.currentQuote = newActiveQuoteIndex;
      if (setNewRoomIndex) {
        store.dispatch(setActiveQuoteIndex(newActiveQuoteIndex));
      }
    } else if (!existingCartRoom && (newCartItem.partId !== "room")) { // we should add room cart item in current quote
      const roomCartItem = getRoomCartItemForCategoryName(currentRoomName, fabricatorState.settings);
      if (roomCartItem) {
        const newRoomCartItem = Object.assign({}, roomCartItem, {
          currentQuote: Number.isInteger(newCartItem.currentQuote) ? newCartItem.currentQuote : activeQuoteIndex
        })
        newCartItems.push(newRoomCartItem);
        createdRoomCartItems.push(newRoomCartItem);
      }
    }

  }

  return newCartItems
}


/**
 * update isVisibleInSQFT status
 */
export const updateSQFTCreatedStatusForQuotes = async () => {
  const {quotes} = store.getState().quotesState;
  const updatedQuotes = quotes.map(quote => {
    quote.isVisibleInSQFT = true
    return {...quote}
  })

  store.dispatch(setQuotes(updatedQuotes));
}

/**
 * @param quoteRoomName {string}
 * @param feature {featureInterface}
 */
export const getQtCategoryFromQuoteRoomName = (quoteRoomName, feature = null) => {
  const layout = getCurrentLayout();
  if (!layout.qtCategories) {
    return null
  }
  return layout.qtCategories.find(qtCat => {
    if (feature) {
      return featureIsMatchingQtCatName(feature, qtCat.name, true)
    }
    return qtCat.name.trim().toLowerCase() === quoteRoomName
  });
}

/**
 * @param feature {featureInterface}
 * @param qtCategoryName {string}
 * @param needToLowerCaseName {boolean}
 * @returns {boolean}
 */
export const featureIsMatchingQtCatName = (feature, qtCategoryName = "", needToLowerCaseName = false) => {
  if (!feature || !qtCategoryName) {
    return false
  }
  const checkCatName = needToLowerCaseName ? qtCategoryName.trim().toLowerCase() : qtCategoryName;

  // we need to have both compares here because:
  // bugfix/fix for comparing room names (customDisplayName is 'A - Kitchen', sharedFeatureName is 'Kitchen')
  // bugfix/fix for comparing room names (customDisplayName is 'Shower', sharedFeatureName is 'Bathroom', qtCat - 'Shower')
  const customLowerCasedName = feature.getLowerCasedCustomDisplayName ? feature.getLowerCasedCustomDisplayName() : "";
  const originalLowerCasedName = feature.getLowerCasedSharedFeatureName ? feature.getLowerCasedSharedFeatureName() : "";
  return (customLowerCasedName === checkCatName) || (originalLowerCasedName === checkCatName)
}

/**
 * update qtActiveCategory if room was selected and qtActiveCategory is not set
 * @param quoteIndex {number}
 */
export const checkIfNeedToSetNewCategory = (quoteIndex = 0) => {
  const {fabricatorState, cartState} = store.getState();
  if (fabricatorState.settings.useCategorySwitching) {
    const selectedQuoteRoomCartItem = getCurrentQuoteRoomCartItem(cartState.cartItems, quoteIndex);
    const qtCategory = getQtCategoryFromQuoteRoomName("", selectedQuoteRoomCartItem ? selectedQuoteRoomCartItem.cartFeature : null);
    if (qtCategory) {
      store.dispatch(setQtActiveCategory(qtCategory.name));
      return true
    }
  }
  return false
}

/**
 * here we check if new room is needed if we are adding a new room, but there is already added another room
 * @param newCartItems {[cartItemInterface]}
 * @returns {cartItemInterface}
 */
export const getOtherRoomCartItemThatIsAddedToCurrentQuote = (newCartItems) => {
  if (newCartItems.length !== 1 || newCartItems[0].partId !== "room"
    || (newCartItems[0] && newCartItems[0].cartFeature && newCartItems[0].cartFeature.dbFeature
      && newCartItems[0].cartFeature.dbFeature.customDisplayName
      && newCartItems[0].cartFeature.dbFeature.customDisplayName.includes("- No"))) { // renuit Countertop - No wizard
    return null
  }
  const {cartState, quotesState} = store.getState();
  const addedRoom = getCurrentQuoteRoomCartItem(cartState.cartItems, quotesState.activeQuoteIndex);
  if (addedRoom && (addedRoom.cartFeature.partName !== newCartItems[0].cartFeature.partName)) {
    return addedRoom
  }
  return null
}

/**
 * here we create a new room (quote) and set new cart item quoteIndex
 * @param newRoomCartItem {cartItemInterface}
 * @param oldRoomCartItem {cartItemInterface}
 * @returns {cartItemInterface}
 */
export const addNewQuoteWithNewRoomCartItem = async (newRoomCartItem, oldRoomCartItem) => {
  const {quotesState} = store.getState();
  const {quotes, activeQuoteIndex} = quotesState;

  // visualizer of the quote we are leaving (from its room cart item), and the one the new room is opened in
  const oldQuoteVisualizer = getCommonVisualizerType(getQuoteVisualizerType(activeQuoteIndex));
  const currentVisualizer = getCommonVisualizerType();

  const currentQuote = await updateCurrentQuote();
  const newQuotes = [...currentQuote, {}];
  // console.log("newCreatedRooms", newQuotes);
  const newActiveQuoteIndex = quotes.length; // for example, if length is 2 (we are adding 2 index(0 and 1 already exist))
  newRoomCartItem = Object.assign({}, newRoomCartItem, {
    currentQuote: newActiveQuoteIndex
  })
  store.dispatch(setQuotes(newQuotes));
  store.dispatch(setActiveQuoteIndex(newActiveQuoteIndex));

  const {wizardState, fabricatorState, cartState, sqftModalFixState} = store.getState();
  const {wizardActiveItem, wizardActiveStep, wizardSelectedValues, allWizardsValues} = wizardState;
  if (oldRoomCartItem && wizardActiveItem && Array.isArray(allWizardsValues)) {
    // the old quote keeps its own object in the old visualizer, otherwise the current object is the old quote one
    const visualizerWasSwitched = (oldQuoteVisualizer !== currentVisualizer)
      && allWizardsValues.some(wizObject => wizObject.quoteIndex === activeQuoteIndex
        && !isSameVisualizerWizard(wizObject, currentVisualizer));
    let oldQuoteCartItems = null; // set if old quote cart items were re-linked back to the old room flow

    let newValues = allWizardsValues.map(wizObject => {
      if (wizObject.quoteIndex !== activeQuoteIndex
        || wizObject.wizardItemId !== wizardActiveItem.id
        || !isSameVisualizerWizard(wizObject, currentVisualizer)) {
        return wizObject
      }
      if (visualizerWasSwitched) {
        // this object was created by switchToNewVisualizerType for the new room, the old quote is in another
        // visualizer and keeps its own object. It is moved to the new quote below
        return null
      }
      const selectedValues = wizObject.wizardSelectedValues || {};
      const roomStepKey = selectedValues[wizardActiveStep] && selectedValues[wizardActiveStep].partId === "room"
        ? String(wizardActiveStep)
        : Object.keys(selectedValues).find(key => selectedValues[key] && selectedValues[key].partId === "room");
      const roomStepValue = roomStepKey !== undefined ? selectedValues[roomStepKey] : null;
      if (!roomStepValue || !roomStepValue.selectedFeature || !roomStepValue.selectedFeature.dbFeature
        || roomStepValue.selectedFeature.dbFeature.id === oldRoomCartItem.cartFeature.dbFeature.id) {
        return wizObject // old quote already keeps the old room (e.g. values weren't saved because of conflicts)
      }
      if (Object.keys(selectedValues).length === 1 && fabricatorState.settings.allowVisualizerSwitching) {
        // we don't need to keep one item selected wizard in old quoteIndex
        return null
      }

      // restore the old room in the old quote (the new room goes to the new quote below) and re-assign the
      // next steps values and old quote cart items back to the old room flow, like setWizardSelectedValues does
      const changedStepValues = {
        [roomStepKey]: {
          ...roomStepValue,
          selectedFeature: oldRoomCartItem.cartFeature,
          wizardGroup: roomStepValue.wizardGroup
            ? getCorrectedWizardGroupForFeature(roomStepValue.wizardGroup, oldRoomCartItem.cartFeature)
            : roomStepValue.wizardGroup,
        }
      };
      const oldQuoteValues = {};
      Object.keys(selectedValues).forEach(key => {
        oldQuoteValues[key] = {...selectedValues[key]} // copy, re-assign logic mutates values wizardGroup
      });
      Object.assign(oldQuoteValues, changedStepValues);

      const reassigned = getWizardValuesReassignedToNewFlow(
        wizardActiveItem,
        activeQuoteIndex,
        changedStepValues,
        oldQuoteValues,
        oldQuoteCartItems || cartState.cartItems,
        sqftModalFixState ? sqftModalFixState.isModalFixOpen : false
      );
      if (reassigned.shouldReassignCartItems) {
        oldQuoteCartItems = reassigned.newCartItems;
      }
      return {
        ...wizObject,
        lastVisitedStep: +roomStepKey,
        wizardSelectedValues: reassigned.newWizValues,
      }
    }).filter(wizObject => wizObject);

    if (oldQuoteCartItems) {
      store.dispatch(changeAllCartItems([...oldQuoteCartItems]));
    }

    // new quote starts with the steps up to the current one (room step), next steps cart items stay in the old quote
    const newQuoteWizardValues = {};
    Object.keys(wizardSelectedValues || {}).forEach(key => {
      if (+key <= wizardActiveStep) {
        newQuoteWizardValues[key] = wizardSelectedValues[key]
      }
    });
    newValues = getUpdatedAllWizardValues(
      newValues,
      wizardActiveStep,
      newActiveQuoteIndex,
      wizardActiveItem.id,
      newQuoteWizardValues,
      wizardActiveItem.uniqueHashId
    );
    store.dispatch(updateAllWizardValues(newValues, newQuoteWizardValues));
  }
  return newRoomCartItem
}
